import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const listTablesSkill: Skill = {
  id: "listTables",
  description:
    "Lista las mesas físicas de un restaurante (id, nombre, sección/zona, capacidad). Úsala cuando el usuario pregunte por las mesas o cuando necesites un tableId.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    try {
      const { data } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/tables`,
        ctx.accessToken,
      );
      const items: any[] = data?.items ?? data?.data ?? data ?? [];
      const tables = items.map((t) => ({
        id: t.id,
        name: t.name,
        section: t.section ?? null,
        seats: t.seats,
      }));
      return {
        ok: true,
        result: { count: tables.length, tables },
        humanSummary: `${tables.length} mesa(s).`,
      };
    } catch (err: any) {
      return {
        ok: false,
        error: err?.response?.data?.message ?? "Error obteniendo mesas.",
      };
    }
  },
};

export const createTableSkill: Skill = {
  id: "createTable",
  description:
    "Crea una mesa nueva en el restaurante. Solo owner/admin pueden hacerlo.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string", description: "Identificador (ej. 'Mesa 12')" },
      seats: { type: "number", description: "Capacidad (1-50)" },
      section: { type: "string", description: "Zona opcional (ej. Terraza)" },
    },
    required: ["restaurantId", "name", "seats"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    const seats = Number(args.seats);
    if (!restaurantId || !name || !Number.isInteger(seats) || seats < 1 || seats > 50) {
      return { ok: false, error: "Datos inválidos (name, seats 1-50)." };
    }
    const section = args.section ? String(args.section) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Crear mesa",
      details: [
        { label: "Nombre", value: name },
        { label: "Capacidad", value: `${seats} pers.` },
        ...(section ? [{ label: "Zona", value: section }] : []),
      ],
      confirmLabel: "Crear",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando mesa "${name}"…`,
      successMsg: `✓ Mesa "${name}" creada`,
      restaurantId,
      targetTab: "mesas",
      targetTabLabel: "Mesas",
      run: async () => {
        try {
          const body: Record<string, unknown> = { name, seats };
          if (section) body.section = section;
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/tables`,
            body,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "products", action: "created", restaurantId, id: data?.id, meta: { table: true } });
          return {
            ok: true as const,
            result: { id: data?.id, name, seats, section },
            humanSummary: `Mesa "${name}" creada (${seats} personas).`,
          };
        } catch (err: any) {
          return {
            ok: false as const,
            error: err?.response?.data?.message ?? "Error creando mesa.",
          };
        }
      },
    });
  },
};

export const updateTableSkill: Skill = {
  id: "updateTable",
  description: "Actualiza una mesa existente (renombrar, cambiar capacidad o zona).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      tableId: { type: "string" },
      name: { type: "string" },
      seats: { type: "number" },
      section: { type: "string" },
    },
    required: ["restaurantId", "tableId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const tableId = String(args.tableId ?? "");
    if (!restaurantId || !tableId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) { patch.name = String(args.name); details.push({ label: "Nuevo nombre", value: patch.name as string }); }
    if (Number.isFinite(Number(args.seats))) { patch.seats = Number(args.seats); details.push({ label: "Capacidad", value: `${patch.seats}` }); }
    if (args.section !== undefined) { patch.section = String(args.section); details.push({ label: "Zona", value: patch.section as string || "(sin zona)" }); }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({
      title: "Actualizar mesa",
      details,
      confirmLabel: "Actualizar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando mesa…",
      successMsg: "✓ Mesa actualizada",
      restaurantId,
      targetTab: "mesas",
      targetTabLabel: "Mesas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.patch(
            `/restaurants/${restaurantId}/tables/${tableId}`,
            patch,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { id: data?.id ?? tableId, ...patch },
            humanSummary: "Mesa actualizada.",
          };
        } catch (err: any) {
          return {
            ok: false as const,
            error: err?.response?.data?.message ?? "Error actualizando mesa.",
          };
        }
      },
    });
  },
};

export const deleteTableSkill: Skill = {
  id: "deleteTable",
  description:
    "Elimina una mesa permanentemente. Acción IRREVERSIBLE. No se puede borrar si tiene pedidos activos.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, tableId: { type: "string" } },
    required: ["restaurantId", "tableId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const tableId = String(args.tableId ?? "");
    if (!restaurantId || !tableId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar mesa",
      description: "La mesa será eliminada PERMANENTEMENTE. No se puede deshacer.",
      details: [{ label: "Mesa", value: tableId }],
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Eliminando mesa…",
      successMsg: "✓ Mesa eliminada",
      restaurantId,
      targetTab: "mesas",
      targetTabLabel: "Mesas",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/tables/${tableId}`,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { tableId, deleted: true },
            humanSummary: "Mesa eliminada.",
          };
        } catch (err: any) {
          return {
            ok: false as const,
            error: err?.response?.data?.message ?? "Error eliminando mesa.",
          };
        }
      },
    });
  },
};
